/**
 * Build the GS1 Application Identifier payload that gets embedded in the
 * QR code on every printed in-house food label. Mirror of the backend's
 * `build_gs1_label_payload()` in `escpos_printer.py` so the on-screen
 * 80 mm preview QR encodes the exact same bytes the printer will.
 *
 * Uses the human-readable parenthesised form `(10)…(17)…(20)…(91)…` —
 * compatible with every QR scanner and parsed natively by `lib/gs1.js`.
 */

const STORAGE_CODE = {
  refrigerated: "01",
  frozen:       "02",
  hot_hold:     "03",
  ambient:      "04",
};

function toYYMMDD(iso) {
  if (!iso || iso.length < 10) return "";
  const y = parseInt(iso.slice(0, 4), 10);
  const m = parseInt(iso.slice(5, 7), 10);
  const d = parseInt(iso.slice(8, 10), 10);
  if (!(m >= 1 && m <= 12 && d >= 1 && d <= 31)) return "";
  const yy = String(y % 100).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${yy}${mm}${dd}`;
}

/** `label` matches the shape used by `LabelForm` state. */
export function buildLabelGs1Payload(label) {
  const parts = [];
  const batch = (label?.batch_number || "").trim();
  if (batch) parts.push(`(10)${batch.slice(0, 20)}`);
  const yymmdd = toYYMMDD(label?.use_by_date || "");
  if (yymmdd) parts.push(`(17)${yymmdd}`);
  const code = STORAGE_CODE[(label?.storage || "").toLowerCase()];
  if (code) parts.push(`(20)${code}`);
  const allergens = label?.allergens || [];
  if (allergens.length) {
    const safe = allergens.map((a) => String(a).slice(0, 8)).join("+").slice(0, 50);
    if (safe) parts.push(`(91)${safe}`);
  }
  return parts.join("");
}

/**
 * Build the public Verify-Label deep-link URL embedded in the printed
 * QR code. Mirror of the backend's `build_label_verify_url()` in
 * `escpos_printer.py` so the on-screen 80mm preview QR encodes exactly
 * what the thermal printer will emit.
 *
 *  https://<base>/labels/verify?id=<uuid>&10=<batch>&17=<yymmdd>&20=<store>&91=<allergens>
 *
 * Scanned by a phone camera ⇒ opens the public Verify Label page (no
 * app install, no login). Scanned by a GS1-aware warehouse scanner ⇒
 * the query-string AIs are parseable per the GS1 Digital Link spec.
 */
export function buildLabelVerifyUrl(label, baseUrl = "") {
  const base = (baseUrl || "").replace(/\/+$/, "");
  const id = label?.id || "";
  const batch = (label?.batch_number || "").trim();
  if (!id && !batch) return "";
  const qs = [];
  if (id) qs.push(`id=${encodeURIComponent(id)}`);
  if (batch) qs.push(`10=${encodeURIComponent(batch.slice(0, 20))}`);
  const yymmdd = toYYMMDD(label?.use_by_date || "");
  if (yymmdd) qs.push(`17=${yymmdd}`);
  const code = STORAGE_CODE[(label?.storage || "").toLowerCase()];
  if (code) qs.push(`20=${code}`);
  const allergens = label?.allergens || [];
  if (allergens.length) {
    const safe = allergens.map((a) => String(a).slice(0, 8)).join("+").slice(0, 50);
    if (safe) qs.push(`91=${encodeURIComponent(safe)}`);
  }
  const query = qs.length ? `?${qs.join("&")}` : "";
  return `${base}/labels/verify${query}`;
}
